import { Component, OnInit } from '@angular/core';
import { IotaModalSize, MatDialogRef } from '@iota/iota-ui-components';
import { Store } from '@ngrx/store';
import {
    invoiceCreationProcessCurrentStep,
    invoiceCreationProcessDataValid,
    invoiceCreationProcessError,
    invoiceCreationProcessIsInResultStep,
    invoiceCreationProcessIsInSettingsStep,
    invoiceCreationProcessLatestResult,
    invoiceCreationProcessLoading,
    invoiceCreationProcessSelectedBusinessPartners,
    invoiceCreationProcessSelectedBusinessPartnerType,
    invoiceCreationProcessSelectedInvoicingType,
    invoiceCreationProcessSelectedLocale
} from 'src/app/store/reducer/invoice-creation-process.reducer';
import {
    BusinessPartnerType,
    BusinessPartnerTypeLabels,
    InvoiceCreationLocale,
    InvoiceCreationLocaleLabels,
    InvoiceCreationProcessStepLabels,
    InvoicingType,
    InvoicingTypeLabels
} from '../../constants-and-enums/invoice-creation-process-types';
import { companies, companiesWithRoleCustomer } from 'src/app/store/reducer/company.reducer';
import {
    InitInvoiceCreationForm,
    InvoiceCreationProcessUpsertForm,
    StartInvoiceCreationProcess
} from 'src/app/store/actions/invoice-creation-process.actions';
import { combineLatest, map } from 'rxjs';
import {
    exportArrayToExcel,
    getValueFromStore,
    sortByNameWithAlphabeticCharactersFirst,
    getCompanyName,
    getFirstDeactivatedCompanyIndex,
    isCompanyDeactivated,
    sortCompaniesByDeactivationStatus
} from '../../utils';
import { allCentralRegulators, centralRegulatorsSortedByName } from 'src/app/store/reducer/central-regulators.reducer';
import { InvoiceSkipReason } from '@iota/iota-management-portal-v4-client';

@Component({
    standalone: false,
    selector: 'app-start-invoice-process-dialog',
    templateUrl: './start-invoice-process-dialog.component.html',
    styleUrls: ['./start-invoice-process-dialog.component.scss']
})
export class StartInvoiceProcessDialogComponent implements OnInit {
    public modalOptions = {
        size: IotaModalSize.SIZE_3XL
    };

    centralRegulators$ = this.store.select(centralRegulatorsSortedByName);
    companies$ = this.store.select(companiesWithRoleCustomer).pipe(
        map((companies) => sortByNameWithAlphabeticCharactersFirst(companies)),
        map((companies) => sortCompaniesByDeactivationStatus(companies))
    );

    isInSettingsStep$ = this.store.select(invoiceCreationProcessIsInSettingsStep);
    isInResultStep$ = this.store.select(invoiceCreationProcessIsInResultStep);

    stepLabels = InvoiceCreationProcessStepLabels;
    currentStep$ = this.store.select(invoiceCreationProcessCurrentStep);

    loading$ = this.store.select(invoiceCreationProcessLoading);
    latestResult$ = this.store.select(invoiceCreationProcessLatestResult);
    error$ = this.store.select(invoiceCreationProcessError);

    selectedBusinessPartnerType$ = this.store.select(invoiceCreationProcessSelectedBusinessPartnerType);
    selectedBusinessPartners$ = this.store.select(invoiceCreationProcessSelectedBusinessPartners);
    selectedInvoicingType$ = this.store.select(invoiceCreationProcessSelectedInvoicingType);
    selectedLocale$ = this.store.select(invoiceCreationProcessSelectedLocale);

    selectBusinessPartnersDisabled$ = this.store
        .select(invoiceCreationProcessSelectedBusinessPartnerType)
        .pipe(map((businessPartnerType) => !businessPartnerType));
    selectBusinessPartnersPlaceholder$ = this.store.select(invoiceCreationProcessSelectedBusinessPartnerType).pipe(
        map((businessPartnerType) => {
            if (businessPartnerType === BusinessPartnerType.CENTRAL_REGULATORS) return $localize`Select Central Regulators...`;
            if (businessPartnerType === BusinessPartnerType.CUSTOMERS) return $localize`Select Customers…`;

            return $localize`Select Business Partners…`;
        })
    );
    showCustomersInBusinessPartnerSelection$ = this.store
        .select(invoiceCreationProcessSelectedBusinessPartnerType)
        .pipe(map((businessPartnerType) => businessPartnerType === BusinessPartnerType.CUSTOMERS));
    showCentralRegulatorsInBusinessPartnerSelection$ = this.store
        .select(invoiceCreationProcessSelectedBusinessPartnerType)
        .pipe(map((businessPartnerType) => businessPartnerType === BusinessPartnerType.CENTRAL_REGULATORS));

    businessPartnerTypes = Object.values(BusinessPartnerType);
    businessPartnerTypeLabels = BusinessPartnerTypeLabels;

    invoicingTypes = Object.values(InvoicingType);
    invoicingTypeLabels = InvoicingTypeLabels;

    invoicingLocales = Object.values(InvoiceCreationLocale);
    invoicingLocaleLabels = InvoiceCreationLocaleLabels;

    businessPartnersSelectLabel$ = combineLatest([
        this.store.select(allCentralRegulators),
        this.store.select(companies),
        this.store.select(invoiceCreationProcessSelectedBusinessPartnerType),
        this.store.select(invoiceCreationProcessSelectedBusinessPartners)
    ]).pipe(
        map(([centralRegulators, companies, businessPartnerType, selectedBusinessPartners]) => {
            if (businessPartnerType === BusinessPartnerType.CENTRAL_REGULATORS) {
                return this.getBusinessPartnerSelectLabel(
                    centralRegulators?.filter((centralRegulator) => selectedBusinessPartners.includes(centralRegulator.centralRegulatorId))
                );
            } else if (businessPartnerType === BusinessPartnerType.CUSTOMERS) {
                return this.getBusinessPartnerSelectLabel(
                    companies?.filter((company) => selectedBusinessPartners.includes(company.companyId))
                );
            }

            return '';
        })
    );

    getBusinessPartnerSelectLabel(selectedBusinessPartners: { name?: string }[]): string {
        if (!selectedBusinessPartners || selectedBusinessPartners.length === 0) return '';
        if (selectedBusinessPartners.length === 1) return selectedBusinessPartners[0]?.name;

        return selectedBusinessPartners[0]?.name + ` (+${selectedBusinessPartners.length - 1})`;
    }

    dataValid$ = this.store.select(invoiceCreationProcessDataValid);

    constructor(
        private dialogRef: MatDialogRef<StartInvoiceProcessDialogComponent>,
        private store: Store
    ) {}

    ngOnInit(): void {
        this.store.dispatch(InitInvoiceCreationForm());
    }

    updateBusinessPartnerType(event) {
        this.store.dispatch(InvoiceCreationProcessUpsertForm({ data: { businessPartnerType: event?.target?.value } }));
    }

    updateSelectedBusinessPartners(event) {
        this.store.dispatch(InvoiceCreationProcessUpsertForm({ data: { businessPartnerIds: event.value } }));
    }

    clearSelectedBusinessPartners() {
        this.store.dispatch(InvoiceCreationProcessUpsertForm({ data: { businessPartnerIds: [] } }));
    }

    updateInvoicingType(event) {
        this.store.dispatch(InvoiceCreationProcessUpsertForm({ data: { invoicingType: event?.target?.value } }));
    }

    updateLocale(event) {
        this.store.dispatch(InvoiceCreationProcessUpsertForm({ data: { locale: event?.target?.value } }));
    }

    startInvoiceProcess() {
        this.store.dispatch(StartInvoiceCreationProcess());
    }

    async downloadReport() {
        const result = await getValueFromStore(this.store, invoiceCreationProcessLatestResult);

        const headers = [
            $localize`Central regulator number`,
            $localize`Central regulator`,
            $localize`Customer number`,
            $localize`Customer`,
            $localize`Status`,
            $localize`Reason`,
            $localize`Number of invoice entries`
        ];

        const exportArray = [];

        if (result?.partialResults?.length > 0) {
            result?.partialResults?.forEach((businessPartnerResult) => {
                const exportObj = {};

                exportObj[headers[0]] = businessPartnerResult?.centralRegulatorNumber;
                exportObj[headers[1]] = businessPartnerResult?.centralRegulatorName;
                exportObj[headers[2]] = businessPartnerResult?.companyNumber;
                exportObj[headers[3]] = businessPartnerResult?.companyName;

                if (businessPartnerResult?.skipped) {
                    exportObj[headers[4]] = $localize`Skipped`;
                    exportObj[headers[5]] = this.getSkipReasonLabel(businessPartnerResult?.skipReason);
                } else if (businessPartnerResult?.successful) {
                    exportObj[headers[4]] = $localize`Successful`;
                    exportObj[headers[5]] = '';
                } else {
                    exportObj[headers[4]] = $localize`Failed`;
                    exportObj[headers[5]] = businessPartnerResult?.errorMessage;
                }

                exportObj[headers[6]] = businessPartnerResult?.numberOfCreatedAuditInvoiceGenerations ?? 0;

                exportArray.push(exportObj);
            });
        } else {
            const exportObj = {};
            headers.forEach((header) => {
                exportObj[header] = '';
            });
            exportArray.push(exportObj);
        }

        exportArrayToExcel(exportArray);
    }

    private getSkipReasonLabel(skipReason: InvoiceSkipReason) {
        if (skipReason === InvoiceSkipReason.INVOICE_IN_REVIEW) return $localize`Business partner has an invoice in review`;
        if (skipReason === InvoiceSkipReason.NO_LICENSE_ACTIVATIONS) return $localize`Business partner has no license activations`;
        if (skipReason === InvoiceSkipReason.POOLED_PAYMENTS_ACTIVATED) return $localize`Business partner has pooled payments activated`;
        if (skipReason === InvoiceSkipReason.CENTRAL_REGULATOR_INVALID_INVOICING_TYPE)
            return $localize`Central regulator has an invalid invoicing type`;

        return '';
    }

    getCompanyName = getCompanyName;
    getFirstDeactivatedIndex = getFirstDeactivatedCompanyIndex;
    isCompanyDeactivated = isCompanyDeactivated;

    closeDialog() {
        this.dialogRef.close();
    }
}
